/**
 * The ONE first-destination resolver (W2 §9.2).
 *
 * Four surfaces used to answer "where does this citizen go first" and they
 * answered it four different ways: the accept page routed `testit` → /testit and
 * everything else → /dashboard; the pending-invitation dialog bounced back to
 * the accept page for a second click; and both intro carousels hard-coded the
 * journey builder, so the most prominent button in the product offered "build my
 * journey" to people whose journey already existed.
 *
 * This is the decision, in one place, pure and platform-neutral. It returns a
 * discriminated union and never an href — see `PostOnboardingDestination`.
 */

import type { InvitationKind, LifecycleState } from '@tonnie/types';
import type { FirstRunJourney, PostOnboardingDestination, PostOnboardingInput } from './types';

/**
 * Membership table for the invitation kinds, keyed by the canonical union so a
 * third kind is a COMPILE error here (a missing property), not a silent `null`
 * that routes an invitee to the wrong hero. `@tonnie/types`' own `isInvitationKind`
 * is deliberately not imported: that would be a runtime import, and this package
 * carries no runtime dependencies.
 */
const INVITATION_KIND_MEMBERS: Record<InvitationKind, true> = {
  full_checkup: true,
  testit: true,
};

/**
 * Lifecycle states in which the citizen still has setup to finish. Keyed by the
 * canonical union for the same reason: a new lifecycle state cannot be added to
 * the platform without this file being forced to say which side of the line it
 * falls on.
 */
const SETUP_UNFINISHED: Record<LifecycleState, boolean> = {
  draft: true,
  pending_setup: true,
  active: false,
  paused: false,
  expired: false,
  completed: false,
  cancelled: false,
};

function isInvitationKind(value: unknown): value is InvitationKind {
  return (
    typeof value === 'string' &&
    Object.prototype.hasOwnProperty.call(INVITATION_KIND_MEMBERS, value)
  );
}

/**
 * Read a journey's `invitationKind` — what the companies on it ask for, which
 * the server derives from the journey's live relationships (full CheckUp wins;
 * `null` when no company's invitation is in play). Anything that is not one of
 * the known kinds reads as "no invitation in play" — an unrecognised value
 * must never be guessed into one of them.
 */
export function invitationKindOf(value: unknown): InvitationKind | null {
  return isInvitationKind(value) ? value : null;
}

/**
 * Which journey the decision is about: the citizen's primary if they have one,
 * otherwise the first row the API returned (`GET /journeys` orders by
 * `created_at DESC`). One picker, so no two surfaces can pick differently.
 */
function pickJourney(journeys: readonly FirstRunJourney[]): FirstRunJourney | null {
  const primary = journeys.find((journey) => journey.isPrimary === true);
  if (primary) return primary;
  return journeys.length > 0 ? journeys[0] : null;
}

/** An unhandled invitation kind is unreachable — and a compile error if added. */
function unreachableInvitationKind(kind: never): never {
  throw new Error(`Unhandled invitation kind: ${String(kind)}`);
}

function destinationForInvitation(kind: InvitationKind): PostOnboardingDestination {
  switch (kind) {
    case 'full_checkup':
      return { kind: 'checkup' };
    case 'testit':
      return { kind: 'testit' };
    default:
      return unreachableInvitationKind(kind);
  }
}

function hasUnfinishedSetup(lifecycleState: LifecycleState | null | undefined): boolean {
  if (!lifecycleState) return false;
  return SETUP_UNFINISHED[lifecycleState];
}

/**
 * Where a citizen goes first: the hero an invitation asks for, the journey
 * whose setup never finished, the Journey Builder when there is no journey, or
 * the dashboard. No portal has a setup gate in front of it (the web since
 * 26 Sep, the phone since 27 Sep · D-M5): a company journey's residence is
 * asked where the journey opens.
 */
export function resolvePostOnboardingDestination(
  input: PostOnboardingInput
): PostOnboardingDestination {
  const journey = pickJourney(input.journeys);

  // An explicitly-known kind (accept page, pending-invitation dialog) wins;
  // otherwise it is the journey's own — what its companies ask for.
  const invitationKind =
    input.invitationKind ?? (journey ? invitationKindOf(journey.invitationKind) : null);

  if (invitationKind) return destinationForInvitation(invitationKind);

  // No invitation in play — the citizen's own journey decides.
  if (!journey) return { kind: 'build-journey' };
  if (hasUnfinishedSetup(journey.lifecycleState)) {
    return { kind: 'resume-journey', journeyId: journey.id };
  }
  return { kind: 'dashboard' };
}
